/**
 * VIBE_CODING_STANDARD
 *
 * -----
 * VERSION_HISTORY:
 * 1.0.0:gemini-1.5-flash (CURRENT) - Initial creation of the UpdateConflictResolver component.
 *
 * -----
 * SECURITY_RISK: LOW - This component displays conflict details and allows users to select which changes to ignore.
 *                      It does not directly handle sensitive data or perform write operations, but prepares data for an API call.
 * INTENT: Provide a clear and interactive interface for users to resolve conflicts detected during a version update,
 *         allowing them to selectively ignore changes from the base version and preserve their current local changes.
 * PROMPTER_INSTRUCTIONS: Ensure the table clearly shows before/after states and that the checkbox logic
 *                        correctly identifies which record IDs to ignore. The "Save" button should correctly
 *                        pass the ignored IDs to the parent component.
 * LLM_CONFIDENCE: 100% - Standard React component for displaying tabular data with interactive elements.
 */
import React, { useState, useCallback, useMemo } from 'react';
import { Button } from './ui/button.tsx';
import { Checkbox } from './ui/checkbox.tsx';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from './ui/table.tsx';
import { Spinner } from './ui/spinner.tsx';
import { VersionConflictEntry, VersionConflictRecordState } from '../types/costwand.d';

interface UpdateConflictResolverProps {
  conflicts: VersionConflictEntry;
  onResolve: (ignoredRecordIds: number[]) => void;
  isSubmitting: boolean;
  onCancel: () => void;
}

const UpdateConflictResolver: React.FC<UpdateConflictResolverProps> = ({
  conflicts,
  onResolve,
  isSubmitting,
  onCancel,
}) => {
  // State to keep track of which record IDs are checked (to be ignored)
  const [checkedRecordIds, setCheckedRecordIds] = useState<Set<number>>(new Set());

  // Memoize the flattened list of conflicts for rendering
  const flattenedConflicts = useMemo(() => {
    const flattened: {
      hash: string;
      tableName: string;
      recordId: number;
      field: string;
      baseValue: any;
      currentValue: any;
    }[] = [];

    for (const hash in conflicts) {
      if (Object.prototype.hasOwnProperty.call(conflicts, hash)) {
        const [before, after] = conflicts[hash];
        const recordId = before.id;
        const tableName = hash.split('-')[0]; // Assuming table_name is present in the state objects

        // Compare fields to find differences
        for (const field in before) {
          // Skip 'id' and 'table_name' as they are identifiers, not conflicting data fields
          if (field === 'id' || field === 'table_name') continue;

          if (Object.prototype.hasOwnProperty.call(before, field) && before[field] !== after[field]) {
            flattened.push({
              hash,
              tableName,
              recordId,
              field,
              baseValue: before[field],
              currentValue: after[field],
            });
          }
        }
      }
    }
    return flattened;
  }, [conflicts]);

  // Handle checkbox change for a record ID
  const handleCheckboxChange = useCallback((recordId: number, checked: boolean) => {
    setCheckedRecordIds(prev => {
      const newSet = new Set(prev);
      if (checked) {
        newSet.add(recordId);
      } else {
        newSet.delete(recordId);
      }
      return newSet;
    });
  }, []);

  // Handle the "Save" button click
  const handleSubmit = useCallback(() => {
    onResolve(Array.from(checkedRecordIds));
  }, [checkedRecordIds, onResolve]);

  return (
    <div className="space-y-4">
      <p className="text-sm text-muted-foreground">
        The following conflicts were detected when trying to update your version with changes from 'Main'.
        For each conflicting record, you can choose to <b>select Current value</b> for that record by ticking the checkbox. Unchecked boxes mean the <b>Base</b> value will persist.
      </p>

      {flattenedConflicts.length === 0 ? (
        <p className="text-muted-foreground">No specific conflicts to display.</p>
      ) : (
        <div className="rounded-md border overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-[50px]">Select Current Value</TableHead>
                <TableHead>Table</TableHead>
                <TableHead>Record ID</TableHead>
                <TableHead>Field</TableHead>
                <TableHead className="bg-green-100">Base Change</TableHead>
                <TableHead className="bg-red-100">Current Change</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {flattenedConflicts.map((conflict, index) => (
                <TableRow key={`${conflict.hash}-${conflict.field}-${index}`}>
                  <TableCell>
                    <Checkbox
                      checked={checkedRecordIds.has(conflict.hash)}
                      onCheckedChange={(checked) => handleCheckboxChange(conflict.hash, !!checked)}
                      disabled={isSubmitting}
                    />
                  </TableCell>
                  <TableCell>{conflict.tableName}</TableCell>
                  <TableCell>{conflict.recordId}</TableCell>
                  <TableCell>{conflict.field}</TableCell>
                  <TableCell className="bg-green-100">{String(conflict.baseValue)}</TableCell>
                  <TableCell className="bg-red-100">{String(conflict.currentValue)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      <div className="flex justify-end gap-2 mt-4">
        <Button variant="outline" onClick={onCancel} disabled={isSubmitting}>
          Cancel
        </Button>
        <Button onClick={handleSubmit} disabled={isSubmitting}>
          {isSubmitting ? <span className="flex items-center gap-2"><Spinner size="sm" /> Saving...</span> : 'Save & Apply Update'}
        </Button>
      </div>
    </div>
  );
};

export default UpdateConflictResolver;